import { useIntegrationsQuery } from '@/hooks/queries/use-integrations-query';
import type { Integration } from '@/types/integration';
import { PlatformEnum } from '@fluenceur/types';
import { useMemo } from 'react';

export type EmailIntegrationStatus = 'connected' | 'needs_reconnect' | 'not_connected';

export const getConnectedEmailIntegration = (integrations?: Integration[] | null) =>
  integrations?.find(
    (integration) =>
      (integration.platform === PlatformEnum.Gmail ||
        integration.platform === PlatformEnum.Outlook) &&
      integration.isConnected
  ) ?? null;

export const getEmailIntegrationStatus = (
  integration?: Integration | null
): EmailIntegrationStatus => {
  if (!integration) return 'not_connected';
  if (integration.needsRefresh) return 'needs_reconnect';
  return 'connected';
};

export const useEmailIntegrationStatus = () => {
  const { data: integrations, ...rest } = useIntegrationsQuery();
  const emailIntegration = useMemo(
    () => getConnectedEmailIntegration(integrations),
    [integrations]
  );
  const emailIntegrationStatus = getEmailIntegrationStatus(emailIntegration);

  return {
    ...rest,
    emailIntegration,
    emailIntegrationStatus,
    /**
     * An integration EXISTS (possibly needing a reconnect). Presence check
     * only — never gate a SEND capability on it: a `needs_reconnect` token
     * cannot actually send.
     */
    hasConnectedEmailIntegration: emailIntegrationStatus !== 'not_connected',
    /**
     * Strict send capability: only a HEALTHY mailbox can send from the brand's
     * address. Every compose / send gate must use this one.
     */
    hasUsableMailbox: emailIntegrationStatus === 'connected',
    needsReconnectEmailIntegration: emailIntegrationStatus === 'needs_reconnect',
  };
};
